import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { ColumnDef, DataTable } from '../../Components/Table/DataTable';
import { CompanySelect } from '../../Components/Select/CompanySelect';
import { LocationSelect } from '../../Components/Select/LocationSelect';
import { Header } from '../../Components/Header/Header';
import {
  MapStatusChip,
  MappingStatusFilter,
  MappingTypeTabs,
  mappingActionLinkSx,
  mappingCellText,
  mappingEmptyStateSx,
  mappingToolbarSx,
} from '../../Components/Vending/MappingShared';
import useGetCustomData from '../../Hooks/useGetCustomData';
import vendingApiClient from '../../Services/VendingApi';
import { formatUTC } from '../../Services/FormatUtc';
import { PageContainer } from '../../Styles/AppStyles';
import { COLORS } from '../../Styles/Colors';
import {
  MachineMappingListResponse,
  MachineMappingRow,
  MachineMappingSuggestion,
} from '../../Types/vending';

type MapStatusFilter = 'unmapped' | 'mapped' | 'all';

const addressDisplay = (row: MachineMappingRow) => {
  const parts = [row.address1, row.city, row.state, row.postal_code].filter(Boolean);
  return parts.length ? parts.join(', ') : '—';
};

export const VendingMachineMappingPage = () => {
  const [rows, setRows] = useState<MachineMappingRow[]>([]);
  const [companies] = useGetCustomData('/api/company/userregister');
  const [selectedCid, setSelectedCid] = useState('');
  const [locationData] = useGetCustomData(
    selectedCid ? `/api/location/${selectedCid}` : '',
  );
  const activeLocations = useMemo(
    () => (Array.isArray(locationData)
      ? locationData.filter((loc: { IsActive?: boolean | number }) => Number(loc.IsActive) === 1)
      : []),
    [locationData],
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<MapStatusFilter>('all');
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selected, setSelected] = useState<MachineMappingRow | null>(null);
  const [selectedOvid, setSelectedOvid] = useState('');

  const loadMappings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: '200', location_map_status: statusFilter });
      if (search.trim()) params.set('q', search.trim());
      const response = await vendingApiClient(`/mappings/machines?${params.toString()}`);
      const json: MachineMappingListResponse = await response.json();
      setRows(json.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load machine mappings');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  useEffect(() => {
    loadMappings();
  }, [loadMappings]);

  const openMapDialog = (row: MachineMappingRow, suggestion?: MachineMappingSuggestion) => {
    setSelected(row);
    setSelectedCid(suggestion ? String(suggestion.cid) : (row.cid ? String(row.cid) : ''));
    setSelectedOvid(suggestion ? String(suggestion.ovid) : (row.ovid ? String(row.ovid) : ''));
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setSelected(null);
    setSelectedCid('');
    setSelectedOvid('');
  };

  const submitMapping = async (body: Record<string, unknown>) => {
    if (!selected) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await vendingApiClient(`/mappings/machines/${selected.id}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      });
      closeDialog();
      setSuccess('Machine mapping saved.');
      await loadMappings();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save mapping');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => {
    if (!selectedCid || !selectedOvid) return;
    submitMapping({ cid: Number(selectedCid), ovid: Number(selectedOvid) });
  };

  const handleQuickAction = useCallback(async (row: MachineMappingRow, body: Record<string, unknown>) => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await vendingApiClient(`/mappings/machines/${row.id}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      });
      setSuccess(body.action === 'unmap' ? 'Machine mapping cleared.' : 'Machine mapping updated.');
      await loadMappings();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update mapping');
    } finally {
      setSaving(false);
    }
  }, [loadMappings]);

  const columns: ColumnDef<MachineMappingRow>[] = useMemo(() => [
    {
      field: 'machine_name',
      headerName: 'Machine',
      width: 180,
      valueGetter: (_v, row) => row.machine_name || `VM-${row.provider_machine_id}`,
      renderCell: (params) => (
        <Typography sx={{ ...mappingCellText, fontWeight: 600 }}>
          {params.row.machine_name || `VM-${params.row.provider_machine_id}`}
        </Typography>
      ),
    },
    {
      field: 'provider_machine_id',
      headerName: 'Vendor ID',
      width: 100,
      renderCell: (params) => (
        <Typography sx={mappingCellText}>{params.row.provider_machine_id}</Typography>
      ),
    },
    {
      field: 'city',
      headerName: 'Vendor Address',
      width: 260,
      valueGetter: (_v, row) => addressDisplay(row),
      renderCell: (params) => (
        <Typography sx={mappingCellText} title={addressDisplay(params.row)}>
          {addressDisplay(params.row)}
        </Typography>
      ),
    },
    {
      field: 'location_map_status',
      headerName: 'Status',
      width: 110,
      renderCell: (params) => (
        <MapStatusChip status={params.row.location_map_status ?? params.value} />
      ),
    },
    {
      field: 'customer_label',
      headerName: 'AIMS Customer · Location',
      width: 260,
      valueGetter: (_v, row) => (
        row.location_map_status === 'mapped'
          ? (row.customer_label || [row.customer_name, row.location_name].filter(Boolean).join(' · ') || 'Mapped')
          : '—'
      ),
      renderCell: (params) => {
        const label = params.row.location_map_status === 'mapped'
          ? (params.row.customer_label
            || [params.row.customer_name, params.row.location_name].filter(Boolean).join(' · ')
            || 'Mapped')
          : '—';
        return <Typography sx={mappingCellText} title={label}>{label}</Typography>;
      },
    },
    {
      field: 'id',
      headerName: '',
      width: 140,
      sortable: false,
      renderCell: (params) => {
        const suggestion = params.row.suggestions?.[0];
        return (
          <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'center' }}>
            {params.row.location_map_status !== 'mapped' && (
              <Typography
                component="button"
                disabled={saving}
                onClick={(e) => {
                  e.stopPropagation();
                  openMapDialog(params.row);
                }}
                sx={mappingActionLinkSx}
              >
                Map
              </Typography>
            )}
            {suggestion && params.row.location_map_status !== 'mapped' && (
              <Typography
                component="button"
                disabled={saving}
                onClick={(e) => {
                  e.stopPropagation();
                  openMapDialog(params.row, suggestion);
                }}
                sx={mappingActionLinkSx}
              >
                Suggest
              </Typography>
            )}
            {params.row.location_map_status === 'mapped' && (
              <Typography
                component="button"
                disabled={saving}
                onClick={(e) => {
                  e.stopPropagation();
                  handleQuickAction(params.row, { action: 'unmap' });
                }}
                sx={mappingActionLinkSx}
              >
                Unmap
              </Typography>
            )}
          </Box>
        );
      },
    },
  ], [handleQuickAction, saving]);

  return (
    <PageContainer>
      <Header
        title="Mappings"
        subtitle="Map vendor machines to AIMS customer and location"
        count={rows.length}
      >
        <Button
          variant="outlined"
          onClick={loadMappings}
          disabled={loading || saving}
          sx={{
            borderColor: COLORS.teal,
            color: COLORS.teal,
            textTransform: 'none',
            '&:hover': { borderColor: COLORS.darkTeal, backgroundColor: `${COLORS.teal}0a` },
          }}
        >
          Refresh
        </Button>
      </Header>

      <MappingTypeTabs active="machines" refreshKey={success} />

      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Box sx={mappingToolbarSx}>
          <MappingStatusFilter<MapStatusFilter>
            value={statusFilter}
            onChange={setStatusFilter}
            options={[
              { value: 'all', label: 'All' },
              { value: 'unmapped', label: 'Unmapped' },
              { value: 'mapped', label: 'Mapped' },
            ]}
          />

          <TextField
            size="small"
            label="Search"
            placeholder="Machine name, city, serial…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && loadMappings()}
            sx={{ minWidth: 280, maxWidth: 420 }}
          />

          {success && (
            <Alert severity="success" sx={{ mt: 2, borderRadius: 2 }} onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
        </Box>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress sx={{ color: COLORS.teal }} />
          </Box>
        )}

        {!loading && rows.length === 0 && !error && (
          <Box sx={mappingEmptyStateSx}>
            <Typography variant="h6" fontWeight={700} sx={{ color: COLORS.darkGray, mb: 1 }}>
              No machines to map
            </Typography>
            <Typography variant="body2" sx={{ color: COLORS.lightGray }}>
              Run a machines sync from Integration Health, then map them to AIMS locations here.
            </Typography>
          </Box>
        )}

        {!loading && rows.length > 0 && (
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <DataTable
              columns={columns}
              data={rows.map((r) => ({ ...r, id: r.id }))}
              pageKey={`vending-machine-mapping-${statusFilter}`}
              idField="id"
              loading={loading}
              rowHeight={48}
            />
          </Box>
        )}
      </Box>

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>Map machine to AIMS location</DialogTitle>
        <DialogContent>
          {selected && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <Box>
                <Typography variant="subtitle2" sx={{ color: COLORS.lightGray, fontWeight: 700 }}>
                  Machine
                </Typography>
                <Typography fontWeight={600} sx={{ color: COLORS.darkGray }}>
                  {selected.machine_name || `VM-${selected.provider_machine_id}`}
                </Typography>
                <Typography variant="body2" sx={{ color: COLORS.lightGray }}>
                  {addressDisplay(selected)}
                </Typography>
              </Box>

              {selected.suggestions.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ color: COLORS.lightGray, fontWeight: 700, mb: 1 }}>
                    Suggested matches
                  </Typography>
                  {selected.suggestions.map((s) => (
                    <Button
                      key={`${s.cid}-${s.ovid}`}
                      size="small"
                      variant="outlined"
                      sx={{
                        mr: 1,
                        mb: 1,
                        textTransform: 'none',
                        borderColor: `${COLORS.lightGray}80`,
                        color: COLORS.teal,
                        '&:hover': { borderColor: COLORS.teal, backgroundColor: `${COLORS.teal}0a` },
                      }}
                      onClick={() => {
                        setSelectedCid(String(s.cid));
                        setSelectedOvid(String(s.ovid));
                      }}
                    >
                      {s.customer_name} · {s.location_name}
                    </Button>
                  ))}
                </Box>
              )}

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Typography variant="subtitle2" sx={{ color: COLORS.lightGray, fontWeight: 700 }}>
                  Customer
                </Typography>
                <CompanySelect
                  companies={companies}
                  defaultName="Select Company"
                  selectedCompany={selectedCid}
                  onChange={(newCompany: string | null) => {
                    setSelectedCid(newCompany ? String(newCompany) : '');
                    setSelectedOvid('');
                  }}
                  sx={{ width: '100%' }}
                />
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Typography variant="subtitle2" sx={{ color: COLORS.lightGray, fontWeight: 700 }}>
                  Location
                </Typography>
                <LocationSelect
                  ovs={activeLocations}
                  defaultName="Select Location"
                  selectedOvs={selectedOvid}
                  onChange={(newOvid: string | number) => setSelectedOvid(String(newOvid))}
                  disabled={!selectedCid}
                  sx={{ width: '100%' }}
                />
              </Box>

              {selected.location_mapped_at && (
                <Typography variant="caption" sx={{ color: COLORS.lightGray }}>
                  Last mapped {formatUTC(selected.location_mapped_at)} UTC
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeDialog} disabled={saving} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !selectedCid || !selectedOvid}
            sx={{
              backgroundColor: COLORS.teal,
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': { backgroundColor: COLORS.darkTeal, boxShadow: 'none' },
            }}
          >
            {saving ? 'Saving…' : 'Save mapping'}
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export default VendingMachineMappingPage;
